/**
 * PublicMenu — Menú digital para clientes (acceso por QR)
 *
 * Mejoras Dufi Gelato:
 *  • Estética de Helados y Dulces Premium (Vainilla, Caramelo, Fresa Pastel)
 *  • Grid de 2 Columnas responsivo
 *  • Fotos de productos con fallback elegante y dulce
 *  • Notas por ítem al agregar al carrito
 *  • Seguimiento del pedido en tiempo real
 */

import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getProducts } from '../services/ProductService';
import { getCategories } from '../services/CategoryService';
import { getTable } from '../services/TableService';
import { api } from '../utils/api';
import { formatCurrency } from '../utils/format-currency';
import { safeArray } from '../utils/safe';
import { getBusinessConfig } from '../hooks/useBusinessConfig';
import {
  ShoppingBag, ChevronLeft, Plus, Minus, Trash2, CheckCircle2,
  UtensilsCrossed, MessageSquare, X, ChefHat, Clock, Truck, Star,
} from 'lucide-react';
import { toast } from 'react-toastify';
import FlavorSelectorModal from '../components/Flavors/FlavorSelectorModal';
import type { Product } from '../types/@types/product';
import { getImageUrl } from '../utils/url';

// ── Helpers ───────────────────────────────────────────────────────────────────

function productImageUrl(product: Product): string | null {
  if (!product.imageUrl && !product.image_path) return null;
  return getImageUrl(product.imageUrl || (product.image_path ? `uploads/${product.image_path}` : ''));
}

// ── Order status tracking ──────────────────────────────────────────────────────

const STATUS_STEPS = [
  { key: 'WAITING',       label: 'Recibido',       icon: CheckCircle2, color: 'text-[#ff6b8b]'   },
  { key: 'IN_PRODUCTION', label: 'Preparando',    icon: ChefHat,      color: 'text-amber-500'   },
  { key: 'DONE',          label: '¡Listo!',         icon: Star,         color: 'text-emerald-500' },
  { key: 'DELIVERED',     label: 'Entregado',       icon: Truck,        color: 'text-indigo-500'  },
];

function OrderTracking({ orderId, tableNumber, onNewOrder }: {
  orderId: number;
  tableNumber?: string | number;
  onNewOrder: () => void;
}) {
  const [status, setStatus] = useState('WAITING');

  // Poll every 6s
  useEffect(() => {
    const poll = async () => {
      try {
        const { data } = await api.get(`/public/order/${orderId}/status`);
        setStatus(data.status);
      } catch { /* ignore */ }
    };
    poll();
    const iv = setInterval(poll, 6000);
    return () => clearInterval(iv);
  }, [orderId]);

  const currentIdx = STATUS_STEPS.findIndex(s => s.key === status);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[#fbf9f5] p-8 text-center text-[#3c2f2f]">
      {/* Animated check */}
      <div className="mb-8 flex h-28 w-28 items-center justify-center rounded-full bg-[#ff6b8b]/10 text-[#ff6b8b]">
        <CheckCircle2 size={72} className="animate-[pulse_2s_ease-in-out_infinite]" />
      </div>

      <h1 className="mb-1 text-3xl font-black bg-gradient-to-r from-[#ff6b8b] to-[#f39c12] bg-clip-text text-transparent">
        ¡Pedido #{orderId} enviado!
      </h1>
      <p className="text-[#6e5d5d] mb-10">Mesa {tableNumber} · Estamos preparando tu helado y postres</p>

      {/* Status steps */}
      <div className="w-full max-w-sm space-y-3 mb-12">
        {STATUS_STEPS.map((step, idx) => {
          const Icon = step.icon;
          const done = idx <= currentIdx;
          const active = idx === currentIdx;
          return (
            <div
              key={step.key}
              className={`flex items-center gap-4 rounded-2xl px-5 py-4 border transition-all ${
                active ? 'bg-white border-[#ff6b8b]/30 shadow-[0_8px_30px_rgba(60,47,47,0.06)] scale-[1.02]' :
                done   ? 'bg-white/60 border-[#ebdcc5]/40' :
                         'opacity-40 border-transparent'
              }`}
            >
              <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                done ? 'bg-[#ff6b8b]/10' : 'bg-[#f3ece0]'
              }`}>
                <Icon size={20} className={done ? step.color : 'text-[#8b7979]'} />
              </div>
              <span className={`font-semibold ${done ? 'text-[#3c2f2f]' : 'text-[#8b7979]'}`}>{step.label}</span>
              {active && (
                <span className="ml-auto flex h-2 w-2 rounded-full bg-[#ff6b8b] animate-pulse" />
              )}
              {done && idx < currentIdx && (
                <CheckCircle2 size={16} className="ml-auto text-emerald-500" />
              )}
            </div>
          );
        })}
      </div>

      <button
        onClick={onNewOrder}
        className="rounded-full bg-gradient-to-r from-[#ff6b8b] to-[#f39c12] px-8 py-3 font-semibold text-white shadow-xl shadow-[#ff6b8b]/20 transition-all hover:brightness-105 active:scale-95"
      >
        Hacer otro pedido
      </button>
    </div>
  );
}

// ── Note input for cart item ──────────────────────────────────────────────────

function NoteModal({ productName, currentNote, onSave, onClose }: {
  productName: string;
  currentNote: string;
  onSave: (note: string) => void;
  onClose: () => void;
}) {
  const [note, setNote] = useState(currentNote);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/40 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-3xl bg-[#fdfbf7] p-6 border border-[#ebdcc5] shadow-2xl">
        <h3 className="text-lg font-bold text-[#3c2f2f] mb-1 flex items-center gap-2">
          <MessageSquare size={18} className="text-[#ff6b8b]" /> Nota para {productName}
        </h3>
        <p className="text-xs text-[#8b7979] mb-4">Ej: extra chocolate, sin barquillo, poca crema...</p>
        <textarea
          ref={inputRef}
          value={note}
          onChange={e => setNote(e.target.value)}
          rows={3}
          maxLength={200}
          placeholder="Sin barquillo, extra salsa..."
          className="w-full rounded-2xl bg-white border border-[#ebdcc5] px-4 py-3 text-sm text-[#3c2f2f] placeholder-[#8b7979]/50 focus:outline-none focus:border-[#ff6b8b] resize-none"
        />
        <div className="flex gap-3 mt-4">
          <button onClick={onClose} className="flex-1 rounded-2xl border border-[#ebdcc5] py-3 text-sm font-semibold text-[#6e5d5d] hover:bg-[#f3ece0]/40">
            Cancelar
          </button>
          <button
            onClick={() => { onSave(note.trim()); onClose(); }}
            className="flex-1 rounded-2xl bg-gradient-to-r from-[#ff6b8b] to-[#f39c12] py-3 text-sm font-bold text-white hover:brightness-105"
          >
            Guardar nota
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Menu ─────────────────────────────────────────────────────────────────

interface CartItem {
  cartKey: string;
  product: Product;
  qty: number;
  flavors: any[];
  price: number;
  note: string;
}

const PublicMenu = () => {
  const { tableId } = useParams<{ tableId: string }>();
  const biz = getBusinessConfig();

  const [cart, setCart] = useState<CartItem[]>([]);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [flavorContext, setFlavorContext] = useState<{ product: Product } | null>(null);
  const [noteContext, setNoteContext] = useState<{ cartKey: string; product: Product; currentNote: string } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [placedOrderId, setPlacedOrderId] = useState<number | null>(null);

  const { data: table } = useQuery({
    queryKey: ['publicTable', tableId],
    queryFn: () => getTable(tableId!),
    enabled: !!tableId,
  });

  const { data: products, isLoading: loadingProducts } = useQuery({
    queryKey: ['publicProducts'],
    queryFn: getProducts,
    staleTime: 5 * 60_000,
  });

  const { data: categories } = useQuery({
    queryKey: ['publicCategories'],
    queryFn: getCategories,
    staleTime: 5 * 60_000,
  });

  const filteredProducts = useMemo(() => {
    const safeProducts = safeArray(products);
    if (!activeCategory) return safeProducts;
    return safeProducts.filter(p =>
      typeof p.category === 'string' ? p.category === activeCategory : (p.category as any)?.id === activeCategory
    );
  }, [products, activeCategory]);

  // ── Cart actions ────────────────────────────────────────────────────────────

  const addToCart = useCallback((product: Product, selectedFlavors: any[] = [], note = '') => {
    const cartKey = `${product.id}-${selectedFlavors.map(f => `${f.id}:${f.qty}`).join(',')}`;
    setCart(prev => {
      const safePrev = safeArray(prev);
      const existing = safePrev.find(i => i.cartKey === cartKey);
      if (existing) {
        return safePrev.map(i => i.cartKey === cartKey ? { ...i, qty: i.qty + 1 } : i);
      }
      const price = product.price + selectedFlavors.reduce((acc, f) => acc + (f.price_delta || 0) * f.qty, 0);
      return [...safePrev, { cartKey, product, qty: 1, flavors: selectedFlavors, price, note }];
    });
    toast.success(`${product.name} agregado`, { autoClose: 1200 });
  }, []);

  const handleProductClick = (product: Product) => {
    const hasFlavors = (product.maxFlavors && product.maxFlavors > 0) || product.flavors?.enabled;
    if (hasFlavors) {
      setFlavorContext({ product });
    } else {
      addToCart(product);
    }
  };

  const removeFromCart = (cartKey: string) => setCart(prev => {
    const safePrev = safeArray(prev);
    return safePrev.filter(i => i.cartKey !== cartKey);
  });

  const updateQty = (cartKey: string, delta: number) => {
    setCart(prev => {
      const safePrev = safeArray(prev);
      return safePrev.map(i => {
        if (i.cartKey !== cartKey) return i;
        const newQty = i.qty + delta;
        if (newQty <= 0) return null as any;
        return { ...i, qty: newQty };
      }).filter(Boolean as any);
    });
  };

  const setItemNote = (cartKey: string, note: string) => {
    setCart(prev => safeArray(prev).map(i => i.cartKey === cartKey ? { ...i, note } : i));
  };

  const safeCart = safeArray(cart);
  const cartTotal = safeCart.reduce((acc, i) => acc + i.price * i.qty, 0);
  const cartCount = safeCart.reduce((acc, i) => acc + i.qty, 0);

  // ── Submit ──────────────────────────────────────────────────────────────────

  const submitOrder = async () => {
    if (safeCart.length === 0) return;
    setIsSubmitting(true);

    // Validación por GPS si está activa
    if (biz?.gpsEnabled) {
      if (!navigator.geolocation) {
        toast.error('Tu navegador no soporta geolocalización. Por favor, realiza el pedido con un mesero.');
        setIsSubmitting(false);
        return;
      }

      toast.info('Verificando tu ubicación física en el local...', { autoClose: 2000 });

      try {
        const position = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 8000,
            maximumAge: 0
          });
        });

        const clientLat = position.coords.latitude;
        const clientLng = position.coords.longitude;

        const bizLat = parseFloat(biz.latitude || '0');
        const bizLng = parseFloat(biz.longitude || '0');
        const maxDist = parseFloat(biz.maxDistance || '100');

        if (bizLat === 0 && bizLng === 0) {
          console.warn('GPS activo pero coordenadas del negocio no configuradas.');
        } else {
          // Haversine distance
          const R = 6371e3; // Earth radius in meters
          const phi1 = (clientLat * Math.PI) / 180;
          const phi2 = (bizLat * Math.PI) / 180;
          const deltaPhi = ((bizLat - clientLat) * Math.PI) / 180;
          const deltaLambda = ((bizLng - clientLng) * Math.PI) / 180;

          const a = Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) +
                    Math.cos(phi1) * Math.cos(phi2) *
                    Math.sin(deltaLambda / 2) * Math.sin(deltaLambda / 2);
          const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
          const distance = R * c;

          if (distance > maxDist) {
            toast.error(`Estás demasiado lejos del local (${Math.round(distance)}m). Para pedir a la mesa debes estar en el establecimiento.`);
            setIsSubmitting(false);
            return;
          }
        }
      } catch (err: any) {
        console.error('Error de geolocalización:', err);
        let errorMsg = 'No pudimos verificar tu ubicación. ';
        if (err.code === 1) {
          errorMsg += 'Por favor, activa los permisos de ubicación en tu navegador para ordenar.';
        } else {
          errorMsg += 'Asegúrate de tener el GPS activado e intenta de nuevo.';
        }
        toast.error(errorMsg);
        setIsSubmitting(false);
        return;
      }
    }

    try {
      const { data } = await api.post('/orders/self-order', {
        tableId,
        items: safeCart.map(i => ({
          product:  i.product.id,
          quantity: i.qty,
          notes:    i.note || undefined,
          flavors:  safeArray(i.flavors).map((f: any) => ({ flavorId: f.id, qty: f.qty })),
        })),
      });
      setPlacedOrderId(data.id);
      setCart([]);
      setIsCartOpen(false);
    } catch {
      toast.error('Error al enviar el pedido. Intenta de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Order tracking screen ────────────────────────────────────────────────────

  if (placedOrderId) {
    return (
      <OrderTracking
        orderId={placedOrderId}
        tableNumber={table?.number ?? tableId}
        onNewOrder={() => setPlacedOrderId(null)}
      />
    );
  }

  // ── Business branding ─────────────────────────────────────────────────────

  const bizName = biz.name || 'Mi Restaurante';
  const bizLogo = biz.logoUrl;

  // Filtrar productos destacados para la sección especial
  const featuredProducts = safeArray(products).filter(p => p.isFeatured || (p as any).is_featured);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-[#fbf9f5] text-[#3c2f2f] selection:bg-[#ff6b8b] selection:text-white relative overflow-x-hidden">
      {/* Soft warm gelato ambient lights */}
      <div className="absolute top-[-10%] left-[-20%] w-[70%] aspect-square rounded-full bg-[#ff6b8b]/5 blur-[120px] pointer-events-none" />
      <div className="absolute top-[20%] right-[-20%] w-[60%] aspect-square rounded-full bg-[#f39c12]/5 blur-[100px] pointer-events-none" />

      {/* ── Header ── */}
      <header className="sticky top-0 z-40 bg-[#fbf9f5]/90 backdrop-blur-xl border-b border-[#ebdcc5]/40 px-5 py-4">
        <div className="flex items-center justify-between max-w-2xl mx-auto">
          <div className="flex items-center gap-3">
            {bizLogo ? (
              <img src={bizLogo} alt={bizName} className="h-11 w-11 rounded-2xl object-contain bg-white p-1 border border-[#ebdcc5]/40" />
            ) : (
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-r from-[#ff6b8b] to-[#f39c12] shadow-lg shadow-[#ff6b8b]/15 text-white">
                <UtensilsCrossed size={20} />
              </div>
            )}
            <div>
              <h1 className="text-base font-black text-[#2d1d17] tracking-tight leading-tight">{bizName}</h1>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                <p className="text-[10px] text-[#ff6b8b] font-bold uppercase tracking-wider">
                  {tableId ? `Mesa ${table?.number ?? tableId}` : 'Carta Digital'}
                </p>
              </div>
            </div>
          </div>
          {tableId && (
            <button
              onClick={() => setIsCartOpen(true)}
              className="relative flex h-11 w-11 items-center justify-center rounded-full bg-white border border-[#ebdcc5]/60 transition-all hover:bg-[#f3ece0]/40 hover:border-[#ff6b8b]/40 active:scale-90"
            >
              <ShoppingBag size={20} className="text-[#3c2f2f]" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 flex h-5.5 w-5.5 items-center justify-center rounded-full bg-[#ff6b8b] text-[10px] font-black text-white border-2 border-[#fbf9f5] shadow-md animate-[bounce_1s_infinite]">
                  {cartCount}
                </span>
              )}
            </button>
          )}
        </div>
      </header>

      {/* ── Categories ── */}
      <div className="sticky top-[76px] z-30 flex gap-2 overflow-x-auto bg-[#fbf9f5]/90 backdrop-blur-xl px-4 py-3 border-b border-[#ebdcc5]/40 no-scrollbar">
        <button
          onClick={() => setActiveCategory(null)}
          className={`flex-shrink-0 whitespace-nowrap rounded-full px-5 py-2 text-xs font-bold transition-all duration-300 ${
            activeCategory === null
              ? 'bg-gradient-to-r from-[#ff6b8b] to-[#f39c12] text-white shadow-lg shadow-[#ff6b8b]/20 scale-105'
              : 'bg-white text-[#6e5d5d] border border-[#ebdcc5]/60 hover:bg-[#f3ece0]/30'
          }`}
        >
          Todo
        </button>
        {safeArray(categories).map((cat: any) => (
          <button
            key={cat.id}
            onClick={() => setActiveCategory(cat.id)}
            className={`flex-shrink-0 whitespace-nowrap rounded-full px-5 py-2 text-xs font-bold transition-all duration-300 ${
              activeCategory === cat.id
                ? 'bg-gradient-to-r from-[#ff6b8b] to-[#f39c12] text-white shadow-lg shadow-[#ff6b8b]/20 scale-105'
                : 'bg-white text-[#6e5d5d] border border-[#ebdcc5]/60 hover:bg-[#f3ece0]/30'
            }`}
          >
            {cat.icon && <span className="mr-1.5">{cat.icon}</span>}{cat.name}
          </button>
        ))}
      </div>

      {/* ── Featured Products (Slider) ── */}
      {featuredProducts.length > 0 && !activeCategory && (
        <div className="px-4 py-6 max-w-2xl mx-auto border-b border-[#ebdcc5]/40">
          <h2 className="text-[11px] font-bold uppercase tracking-widest text-[#ff6b8b] mb-4 flex items-center gap-1.5">
            <Star size={12} className="fill-[#ff6b8b] text-[#ff6b8b]" /> Recomendados de la casa
          </h2>
          <div className="flex gap-4 overflow-x-auto pb-3 no-scrollbar">
            {featuredProducts.map(product => {
              const imgUrl = productImageUrl(product);
              return (
                <div
                  key={`featured-${product.id}`}
                  onClick={() => handleProductClick(product)}
                  className="w-48 shrink-0 cursor-pointer overflow-hidden rounded-[24px] bg-white border border-[#ebdcc5]/60 transition-all duration-300 hover:border-[#ff6b8b]/30 hover:shadow-xl hover:shadow-[#ff6b8b]/5 relative group"
                >
                  <div className="absolute top-2.5 right-2.5 z-10 rounded-full bg-gradient-to-r from-[#ff6b8b] to-[#f39c12] px-2 py-0.5 text-[8px] font-black uppercase text-white flex items-center gap-0.5 shadow-md">
                    <Star size={8} className="fill-white" /> Especial
                  </div>
                  {imgUrl ? (
                    <div className="h-28 overflow-hidden relative">
                      <img
                        src={imgUrl}
                        alt={product.name}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                    </div>
                  ) : (
                    <div className="h-28 flex items-center justify-center bg-gradient-to-br from-[#fff0f3] to-[#fdf0d5] border-b border-[#ebdcc5]/40">
                      <UtensilsCrossed size={22} className="text-[#ff6b8b]/30" />
                    </div>
                  )}
                  <div className="p-3.5">
                    <h3 className="font-bold text-[#3c2f2f] text-xs truncate leading-snug group-hover:text-[#ff6b8b] transition-colors">{product.name}</h3>
                    <p className="mt-1 text-base font-black text-[#ff6b8b] tracking-tighter">{formatCurrency(product.price)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Products ── */}
      <main className="px-4 py-6 pb-36 max-w-2xl mx-auto">
        {loadingProducts ? (
          <div className="flex h-64 flex-col items-center justify-center gap-6">
            <div className="relative h-14 w-14">
              <div className="h-14 w-14 rounded-full border-4 border-[#ff6b8b]/10" />
              <div className="absolute top-0 h-14 w-14 animate-spin rounded-full border-4 border-[#ff6b8b] border-t-transparent" />
            </div>
            <p className="text-[#ff6b8b]/70 font-bold animate-pulse uppercase tracking-wider text-xs">Cargando menú...</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3.5 md:gap-4">
            {filteredProducts.map(product => {
              const imgUrl = productImageUrl(product);
              const cartItem = cart.find(i => i.product.id === product.id);
              const isFeatured = product.isFeatured || (product as any).is_featured;
              return (
                <div
                  key={product.id}
                  className="group relative flex flex-col justify-between overflow-hidden rounded-[24px] bg-white border border-[#ebdcc5]/60 hover:border-[#ff6b8b]/30 transition-all duration-300 shadow-[0_8px_24px_rgba(60,47,47,0.02)] hover:shadow-[0_12px_32px_rgba(60,47,47,0.05)]"
                >
                  {/* Image/Placeholder */}
                  {imgUrl ? (
                    <div className="relative aspect-square overflow-hidden w-full">
                      <img
                        src={imgUrl}
                        alt={product.name}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                        onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                      {isFeatured && (
                        <div className="absolute top-2.5 left-2.5 rounded-full bg-gradient-to-r from-[#ff6b8b] to-[#f39c12] px-2 py-0.5 text-[8px] font-black uppercase text-white flex items-center gap-0.5 shadow-md">
                          <Star size={8} className="fill-white" /> Recomendado
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="relative aspect-square w-full flex items-center justify-center bg-gradient-to-br from-[#fff0f3] to-[#fdf0d5] border-b border-[#ebdcc5]/40 overflow-hidden">
                      <div className="flex flex-col items-center gap-1.5 text-[#ff6b8b]/30">
                        <UtensilsCrossed size={28} strokeWidth={1.5} />
                        <span className="text-[9px] font-black uppercase tracking-wider opacity-60">Delicia</span>
                      </div>
                      {isFeatured && (
                        <div className="absolute top-2.5 left-2.5 rounded-full bg-gradient-to-r from-[#ff6b8b] to-[#f39c12] px-2 py-0.5 text-[8px] font-black uppercase text-white flex items-center gap-0.5 shadow-md">
                          <Star size={8} className="fill-white" /> Recomendado
                        </div>
                      )}
                    </div>
                  )}

                  {/* Content */}
                  <div className="p-3.5 flex flex-col flex-1 justify-between">
                    <div>
                      <h3 className="font-bold text-[#3c2f2f] text-sm md:text-base tracking-tight leading-snug line-clamp-1 group-hover:text-[#ff6b8b] transition-colors">
                        {product.name}
                      </h3>
                      {product.description && (
                        <p className="mt-1 text-xs text-[#8b7979] leading-normal line-clamp-2 min-h-[2rem]">
                          {product.description}
                        </p>
                      )}
                    </div>

                    <div className="mt-4 flex items-center justify-between gap-1">
                      <p className="text-[17px] md:text-lg font-black text-[#ff6b8b] tracking-tighter shrink-0">
                        {formatCurrency(product.price)}
                      </p>

                      {tableId && (
                        cartItem ? (
                          <div className="flex items-center gap-1.5 bg-[#f3ece0]/60 rounded-full p-1 border border-[#ebdcc5]/40 shrink-0">
                            <button
                              onClick={() => updateQty(cartItem.cartKey, -1)}
                              className="flex h-6 w-6 items-center justify-center rounded-full text-[#6e5d5d] hover:text-[#ff6b8b] transition-colors"
                            >
                              {cartItem.qty === 1 ? <Trash2 size={11} /> : <Minus size={11} />}
                            </button>
                            <span className="text-[11px] font-black text-[#3c2f2f] w-4 text-center">{cartItem.qty}</span>
                            <button
                              onClick={() => handleProductClick(product)}
                              className="flex h-6 w-6 items-center justify-center rounded-full bg-[#ff6b8b] text-white"
                            >
                              <Plus size={11} />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleProductClick(product)}
                            className="flex h-8 w-8 items-center justify-center rounded-full bg-[#ff6b8b] text-white shadow-md shadow-[#ff6b8b]/10 hover:scale-105 active:scale-95 transition-all shrink-0"
                            title="Agregar"
                          >
                            <Plus size={15} />
                          </button>
                        )
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {filteredProducts.length === 0 && !loadingProducts && (
              <div className="col-span-2 py-16 flex flex-col items-center justify-center gap-3 text-[#8b7979]">
                <UtensilsCrossed size={36} strokeWidth={1} />
                <p className="font-semibold text-xs">Sin productos en esta categoría</p>
              </div>
            )}
          </div>
        )}
      </main>

      {/* ── Floating Cart Bar ── */}
      {cartCount > 0 && !isCartOpen && (
        <div className="fixed bottom-8 left-1/2 z-40 -translate-x-1/2 w-[92%] max-w-md animate-[slideUp_0.4s_ease-out]">
          <button
            onClick={() => setIsCartOpen(true)}
            className="flex w-full items-center justify-between rounded-[28px] bg-gradient-to-r from-[#ff6b8b] to-[#f39c12] p-2 pl-6 text-white shadow-[0_20px_50px_rgba(255,107,139,0.25)] border border-[#ff6b8b]/10 transition-all active:scale-95 hover:brightness-105"
          >
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-white/70 text-left">Tu pedido</p>
              <p className="font-black text-lg tracking-tight">{cartCount} ítem{cartCount !== 1 ? 's' : ''}</p>
            </div>
            <div className="flex items-center gap-3 bg-black/10 h-14 rounded-[22px] px-5 border border-white/10">
              <p className="text-xl font-black tracking-tighter">{formatCurrency(cartTotal)}</p>
              <ChevronLeft className="rotate-180" size={20} />
            </div>
          </button>
        </div>
      )}

      {/* ── Cart Sheet ── */}
      {isCartOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 backdrop-blur-sm animate-[fadeIn_0.2s_ease-out]">
          <div className="w-full max-w-lg rounded-t-[36px] bg-[#fbf9f5] p-6 pb-10 shadow-[0_-20px_60px_rgba(60,47,47,0.15)] border-t border-[#ebdcc5] max-h-[90dvh] flex flex-col animate-[slideUp_0.3s_ease-out]">
            <div className="mx-auto mb-5 h-1.5 w-14 rounded-full bg-[#ebdcc5]" />

            <div className="flex items-center justify-between mb-6 shrink-0">
              <div>
                <h2 className="text-2xl font-black text-[#3c2f2f] tracking-tight">Tu Pedido</h2>
                <p className="text-xs font-bold text-[#ff6b8b] uppercase tracking-widest mt-0.5">
                  {cartCount} ítem{cartCount !== 1 ? 's' : ''}
                </p>
              </div>
              <button
                onClick={() => setIsCartOpen(false)}
                className="flex h-11 w-11 items-center justify-center rounded-full bg-white border border-[#ebdcc5]/60 text-[#8b7979] hover:text-[#3c2f2f]"
              >
                <X size={20} />
              </button>
            </div>

            {/* Items */}
            <div className="overflow-y-auto flex-1 pr-1 space-y-3 mb-5">
              {safeArray(cart).length === 0 ? (
                <div className="py-16 text-center">
                  <ShoppingBag size={36} className="mx-auto mb-3 text-[#ebdcc5]" />
                  <p className="text-[#8b7979] font-bold uppercase tracking-wider text-sm">Carrito vacío</p>
                </div>
              ) : safeArray(cart).map(item => (
                <div key={item.cartKey} className="rounded-2xl bg-white border border-[#ebdcc5]/40 p-4 hover:border-[#ff6b8b]/20 transition-all shadow-[0_4px_12px_rgba(60,47,47,0.01)]">
                  <div className="flex gap-4">
                    {/* Small image */}
                    {productImageUrl(item.product) ? (
                      <img
                        src={productImageUrl(item.product)!}
                        alt={item.product.name}
                        className="h-14 w-14 rounded-xl object-cover shrink-0 border border-[#ebdcc5]/40"
                      />
                    ) : (
                      <div className="h-14 w-14 shrink-0 rounded-xl bg-[#fff0f3] flex items-center justify-center border border-[#ebdcc5]/40">
                        <UtensilsCrossed size={20} className="text-[#ff6b8b]/30" />
                      </div>
                    )}

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="font-bold text-[#3c2f2f] leading-snug">{item.product.name}</h4>
                        <p className="font-black text-[#ff6b8b] shrink-0">{formatCurrency(item.price * item.qty)}</p>
                      </div>

                      {safeArray(item.flavors).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {safeArray(item.flavors).map((f: any) => (
                            <span key={f.id} className="px-2 py-0.5 rounded-lg bg-[#ff6b8b]/5 text-[10px] text-[#ff6b8b] font-bold border border-[#ff6b8b]/10">
                              {f.name} ×{f.qty}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Note */}
                      {item.note ? (
                        <button
                          onClick={() => setNoteContext({ cartKey: item.cartKey, product: item.product, currentNote: item.note })}
                          className="mt-1.5 flex items-center gap-1 text-[11px] text-[#f39c12] hover:text-[#f39c12]/80"
                        >
                          <MessageSquare size={10} /> {item.note}
                        </button>
                      ) : (
                        <button
                          onClick={() => setNoteContext({ cartKey: item.cartKey, product: item.product, currentNote: '' })}
                          className="mt-1.5 flex items-center gap-1 text-[11px] text-[#8b7979] hover:text-[#3c2f2f]"
                        >
                          <MessageSquare size={10} /> Añadir nota
                        </button>
                      )}

                      {/* Qty controls */}
                      <div className="mt-2 flex items-center gap-2">
                        <button
                          onClick={() => updateQty(item.cartKey, -1)}
                          className="flex h-8 w-8 items-center justify-center rounded-xl bg-[#f3ece0]/40 border border-[#ebdcc5]/40 text-[#6e5d5d] hover:bg-[#ff6b8b]/10 hover:text-[#ff6b8b] transition-colors"
                        >
                          {item.qty === 1 ? <Trash2 size={14} /> : <Minus size={14} />}
                        </button>
                        <span className="w-5 text-center text-sm font-black text-[#3c2f2f]">{item.qty}</span>
                        <button
                          onClick={() => updateQty(item.cartKey, 1)}
                          className="flex h-8 w-8 items-center justify-center rounded-xl bg-[#ff6b8b] text-white shadow-sm transition-all hover:scale-105"
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Total + CTA */}
            <div className="shrink-0 space-y-4 border-t border-[#ebdcc5]/40 pt-4">
              <div className="flex items-center justify-between text-[#3c2f2f]">
                <span className="text-[#8b7979] font-bold uppercase tracking-widest text-xs">Total</span>
                <span className="text-2xl font-black tracking-tighter text-[#ff6b8b]">{formatCurrency(cartTotal)}</span>
              </div>
              <button
                disabled={cart.length === 0 || isSubmitting}
                onClick={submitOrder}
                className="w-full rounded-2xl bg-gradient-to-r from-[#ff6b8b] to-[#f39c12] py-4 font-black text-white shadow-2xl shadow-[#ff6b8b]/15 transition-all hover:brightness-105 active:scale-[0.98] disabled:opacity-50"
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    <span className="uppercase tracking-widest text-sm">Enviando...</span>
                  </div>
                ) : (
                  <span className="uppercase tracking-[0.15em] text-sm">Confirmar pedido</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Flavor modal ── */}
      {flavorContext && (
        <FlavorSelectorModal
          open
          onClose={() => setFlavorContext(null)}
          product={flavorContext.product}
          onConfirm={payload => {
            addToCart(flavorContext.product, payload.flavors);
            setFlavorContext(null);
          }}
        />
      )}

      {/* ── Note modal ── */}
      {noteContext && (
        <NoteModal
          productName={noteContext.product.name}
          currentNote={noteContext.currentNote}
          onSave={note => setItemNote(noteContext.cartKey, note)}
          onClose={() => setNoteContext(null)}
        />
      )}
    </div>
  );
};

export default PublicMenu;
